import React, { useMemo } from 'react';
import { createUseStyles } from 'react-jss';

import { Theme } from '@app/theme';
import { IconChip, LabeledSection } from '@app/core';
import {
  DevicesCategory,
  Participation,
  RequiredMediaSource,
  Session,
  SessionQuota,
  useHaveQuota,
} from '@app/api';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  iconContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.marginBase,
  },
  container: {
    display: 'flex',
    gap: theme.marginBase * 3,
    [theme.media.mobile]: {
      flexDirection: 'column',
    },
  },
}));

interface Props {
  session: Session;
  participation?: Participation;
}

export const SummaryDevices = ({ session, participation }: Props) => {
  const classes = useStyles();
  const haveDeviceQuota = useHaveQuota(
    session?.id,
    SessionQuota.ALLOWED_DEVICES_CATEGORY,
  );
  const { t } = useTranslation();

  const selectedDevice = participation?.selectedDevice;
  const deviceSectionLabel = selectedDevice
    ? t('booked.summary.device')
    : t('booked.summary.authorizedDevices');
  const deviceIconColor =
    haveDeviceQuota && selectedDevice ? 'blue' : 'inkSecondary';
  const devices = useMemo(() => {
    if (haveDeviceQuota && selectedDevice) {
      return [selectedDevice];
    }

    return session?.allowedDevicesCategories;
  }, [haveDeviceQuota, selectedDevice, session]);

  return (
    <>
      {session?.allowedDevicesCategories &&
        session.allowedDevicesCategories.length !== 0 && (
          <div className={classes.container}>
            {devices && devices.length !== 0 && (
              <LabeledSection label={deviceSectionLabel}>
                <div className={classes.iconContainer}>
                  {devices.includes(DevicesCategory.COMPUTER) && (
                    <IconChip
                      icon="ri-computer-line"
                      color={deviceIconColor}
                      alt="Computer"
                      label={t(
                        'application.info.allowedDevicesCategories.COMPUTER',
                      )}
                    />
                  )}
                  {devices.includes(DevicesCategory.TABLET) && (
                    <IconChip
                      icon="ri-tablet-line"
                      color={deviceIconColor}
                      alt="Tablet"
                      label={t(
                        'application.info.allowedDevicesCategories.TABLET',
                      )}
                    />
                  )}
                  {devices.includes(DevicesCategory.SMARTPHONE) && (
                    <IconChip
                      icon="ri-smartphone-line"
                      color={deviceIconColor}
                      alt="Smartphone"
                      label={t(
                        'application.info.allowedDevicesCategories.SMARTPHONE',
                      )}
                    />
                  )}
                </div>
              </LabeledSection>
            )}
            {session?.requiredMediaSource &&
              session.requiredMediaSource.length !== 0 && (
                <LabeledSection label={t('booked.summary.required')}>
                  <div className={classes.iconContainer}>
                    {session.requiredMediaSource.includes(
                      RequiredMediaSource.VIDEO,
                    ) && (
                      <IconChip
                        icon="ri-webcam-line"
                        color="inkSecondary"
                        alt="Webcam"
                        label={t('application.info.requiredMediaSource.VIDEO')}
                      />
                    )}
                    {session.requiredMediaSource.includes(
                      RequiredMediaSource.AUDIO,
                    ) && (
                      <IconChip
                        icon="ri-mic-line"
                        color="inkSecondary"
                        alt="Mic"
                        label={t('application.info.requiredMediaSource.AUDIO')}
                      />
                    )}
                    {session.requiredMediaSource.includes(
                      RequiredMediaSource.SCREEN_SHARING,
                    ) && (
                      <IconChip
                        icon="ri-cast-line"
                        color="inkSecondary"
                        alt="Cast"
                        label={t(
                          'application.info.requiredMediaSource.SCREEN_SHARING',
                        )}
                      />
                    )}
                  </div>
                </LabeledSection>
              )}
          </div>
        )}
    </>
  );
};
